export type CopyResult = 'copied' | 'manual';

/**
 * Put `text` on the clipboard. Tries the async Clipboard API (secure contexts, with permission), then
 * the old `execCommand('copy')` on a throw-away selection; if neither works it answers `'manual'` so
 * the caller can show the text selected and let the visitor copy it themselves. Never throws, never
 * leaves focus behind in a temporary field.
 */
export async function copyText(text: string): Promise<CopyResult> {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return 'copied';
    }
  } catch {
    // denied, not focused, insecure context — fall through to the legacy path
  }
  return legacyCopy(text) ? 'copied' : 'manual';
}

function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.setAttribute('aria-hidden', 'true');
  field.style.cssText =
    'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none';
  document.body.appendChild(field);
  let copied: boolean;
  try {
    field.select();
    field.setSelectionRange(0, text.length);
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  } finally {
    field.remove();
    previous?.focus();
  }
  return copied;
}
